import { useEffect, useState, useRef } from "react";

import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import LinearProgress, {
  linearProgressClasses,
} from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import DeviceUnknownIcon from "@mui/icons-material/DeviceUnknown";
import MobileFriendlyIcon from "@mui/icons-material/MobileFriendly";
import MobileOffIcon from "@mui/icons-material/MobileOff";
import PermDeviceInformationIcon from "@mui/icons-material/PermDeviceInformation";
import { useDrawingArea } from "@mui/x-charts/hooks";
import { PieChart } from "@mui/x-charts/PieChart";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { useRefreshInterval } from "../../contexts/RefreshIntervalContext";
import { getOnlineNodeWidget } from "../../api/dashboardAPI";
import { usePageVisibility } from "../../hooks/usePageVisibility";
import { useApiRequest } from "../../hooks/useApiRequest";

const StyledText = styled("text", {
  shouldForwardProp: (prop) => prop !== "variant",
})(({ theme }) => ({
  textAnchor: "middle",
  dominantBaseline: "central",
  fill: (theme.vars || theme).palette.text.secondary,
  variants: [
    {
      props: {
        variant: "primary",
      },
      style: {
        fontSize: theme.typography.h5.fontSize,
      },
    },
    {
      props: ({ variant }) => variant !== "primary",
      style: {
        fontSize: theme.typography.body2.fontSize,
      },
    },
    {
      props: {
        variant: "primary",
      },
      style: {
        fontWeight: theme.typography.h5.fontWeight,
      },
    },
    {
      props: ({ variant }) => variant !== "primary",
      style: {
        fontWeight: theme.typography.body2.fontWeight,
      },
    },
  ],
}));

function PieCenterLabel({ primaryText, secondaryText }) {
  const { width, height, left, top } = useDrawingArea();
  const primaryY = top + height / 2 - 10;
  const secondaryY = primaryY + 24;

  return (
    <>
      <StyledText variant="primary" x={left + width / 2} y={primaryY}>
        {primaryText}
      </StyledText>
      <StyledText variant="secondary" x={left + width / 2} y={secondaryY}>
        {secondaryText}
      </StyledText>
    </>
  );
}

PieCenterLabel.propTypes = {
  primaryText: PropTypes.string.isRequired,
  secondaryText: PropTypes.string.isRequired,
};

export default function ChartOnlineOffline() {
  const { t } = useTranslation();
  const { isRunning } = useRefreshInterval();
  const isPageVisible = usePageVisibility();
  const [data, setData] = useState({
    onlineNodes: 0,
    lowerThen24offlineNodes: 0,
    greaterThen24offlineNodes: 0,
    totalNodes: 0,
    unAssignedDevices: 0,
  });
  const intervalRef = useRef(null);

  const { loading, error, execute } = useApiRequest({
    handle401: true,
    onSuccess: (result) => {
      setData({
        onlineNodes: result.onlineNodes || 0,
        lowerThen24offlineNodes: result.lowerThen24offlineNodes || 0,
        greaterThen24offlineNodes: result.greaterThen24offlineNodes || 0,
        totalNodes: result.totalNodes || 0,
        unAssignedDevices: result.unAssignedDevices || 0,
      });
    }
  });

  // Initial load
  useEffect(() => {
    if (!isPageVisible) return;

    execute(getOnlineNodeWidget, {
      showLoading: true,
      errorMessage: "Failed to fetch dashboard data"
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto refresh
  useEffect(() => {
    if (isRunning && isPageVisible) {
      // Clear any existing interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      // Start new interval
      intervalRef.current = setInterval(() => {
        execute(getOnlineNodeWidget, {
          showLoading: false,
          errorMessage: "Failed to fetch dashboard data"
        });
      }, 30000);
    } else {
      // Stop interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRunning, isPageVisible]);

  // Listen to custom refresh event
  useEffect(() => {
    const handler = () => {
      if (!isPageVisible) return;

      execute(getOnlineNodeWidget, {
        showLoading: true,
        errorMessage: "Failed to fetch dashboard data"
      });
    };
    window.addEventListener("refreshAllData", handler);
    return () => window.removeEventListener("refreshAllData", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPageVisible]);

  const { onlineNodes, lowerThen24offlineNodes, greaterThen24offlineNodes, totalNodes, unAssignedDevices } = data;

  const computedTotal =
    onlineNodes + lowerThen24offlineNodes + greaterThen24offlineNodes;
  const total = totalNodes || computedTotal;

  const chartData = [
    {
      label: t("dashboard.onlineOffline.online"),
      value: onlineNodes,
      color: "#4CAF50",
    },
    {
      label: t("dashboard.onlineOffline.offlineLess24h"),
      value: lowerThen24offlineNodes,
      color: "#FFC107",
    },
    {
      label: t("dashboard.onlineOffline.offlineMore24h"),
      value: greaterThen24offlineNodes,
      color: "#ff5252",
    },
    {
      label: t("dashboard.onlineOffline.unAssigned", "Atanmamış"),
      value: unAssignedDevices,
      color: "#9E9E9E",
    },
  ].filter((item) => item.value > 0);

  const statsItems = [
    {
      name: t("dashboard.onlineOffline.online"),
      value: onlineNodes,
      color: "#4CAF50",
      icon: <MobileFriendlyIcon />,
    },
    {
      name: t("dashboard.onlineOffline.offlineLess24h"),
      value: lowerThen24offlineNodes,
      color: "#FFC107",
      icon: <PermDeviceInformationIcon />,
    },
    {
      name: t("dashboard.onlineOffline.offlineMore24h"),
      value: greaterThen24offlineNodes,
      color: "#ff5252",
      icon: <MobileOffIcon />,
    },
    {
      name: t("dashboard.onlineOffline.unAssigned", "Atanmamış Terminal"),
      value: unAssignedDevices,
      color: "#9E9E9E",
      icon: <DeviceUnknownIcon />,
    },
  ];

  return (
    <Card
      variant="outlined"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        flexGrow: 1,
        borderColor: "divider",
        borderRadius: 0,
        borderWidth: 1.5,
        overflow: "hidden",
        height: "100%",
      }}
    >
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <PieChart
            margin={{
              left: 80,
              right: 80,
              top: 80,
              bottom: 80,
            }}
            series={[
              {
                data: chartData,
                innerRadius: 65,
                outerRadius: 80,
                paddingAngle: 0,
                highlightScope: { faded: "global", highlighted: "item" },
              },
            ]}
            height={160}
            width={200}
            slotProps={{
              legend: { hidden: true },
            }}
          >
            <PieCenterLabel
              primaryText={String(total)}
              secondaryText={t("dashboard.onlineOffline.title")}
            />
          </PieChart>
        </Box>

        {loading && (
          <Typography variant="body2" sx={{ textAlign: "center", py: 1 }}>
            {t("common.loading")}
          </Typography>
        )}

        {error && (
          <Typography
            variant="body2"
            color="error"
            sx={{ textAlign: "center", py: 1 }}
          >
            {error}
          </Typography>
        )}

        {statsItems.map((item, index) => (
          <Stack
            key={index}
            direction="row"
            sx={{ alignItems: "center", gap: 2, pb: 2 }}
          >
            {item.icon}
            <Stack sx={{ gap: 1, flexGrow: 1 }}>
              <Stack
                direction="row"
                sx={{
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: "500" }}>
                  {item.name}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {item.value}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                aria-label="Node status distribution"
                value={total > 0 ? (item.value / total) * 100 : 0}
                sx={{
                  [`& .${linearProgressClasses.bar}`]: {
                    backgroundColor: item.color,
                  },
                }}
              />
            </Stack>
          </Stack>
        ))}
      </CardContent>
    </Card>
  );
}
